import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { formatPrice } from "@/lib/catalog";

export default function CartPage() {
  const { items, totalItems, totalPrice, updateQuantity, removeItem, clear } =
    useCart();
  const { user } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const currency = items[0]?.currency ?? "USD";

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setCheckingOut(true);
    setError(null);
    try {
      // 1. Crea la compra (encabezado del pedido).
      const { data: order, error: orderError } = await supabase
        .from("order_headers")
        .insert({
          currency,
          payment_provider: "manual",
          status: "pending_payment",
          subtotal_items: totalPrice,
          customer_id: user?.id ?? null,
        })
        .select("id")
        .single();

      if (orderError) throw orderError;

      // 2. Guarda cada artículo del pedido.
      const orderItems = items.map((item) => ({
        order_id: order.id,
        product_id: String(item.productId),
        product_name: item.title,
        quantity: item.quantity,
        unit_price: item.price,
        final_price: item.price,
        subtotal: item.price * item.quantity,
      }));

      const { error: itemsError } = await supabase
        .from("order_items")
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // 3. Vacía el carrito y muestra confirmación.
      clear();
      setSuccess(
        "¡Compra registrada con éxito! El vendedor se pondrá en contacto contigo.",
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudo completar la compra.",
      );
    } finally {
      setCheckingOut(false);
    }
  };

  // Estado: compra completada.
  if (success) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-background-50 px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100">
          <i className="ri-checkbox-circle-fill text-4xl text-secondary-600"></i>
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          ¡Compra registrada!
        </h1>
        <p className="mt-2 max-w-sm text-sm text-foreground-600">{success}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Seguir explorando
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              Carrito de compra
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              {totalItems === 0
                ? "Aún no has agregado artículos."
                : `${totalItems} ${totalItems === 1 ? "artículo" : "artículos"} en tu carrito`}
            </p>
          </div>
          {items.length > 0 && (
            <Link
              to="/"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-500 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
              Seguir comprando
            </Link>
          )}
        </div>

        {items.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-shopping-cart-2-line text-3xl text-primary-600"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              Tu carrito está vacío
            </h2>
            <p className="mt-1 max-w-sm text-sm text-foreground-500">
              Explora el catálogo y agrega artículos que te gusten. Aquí los
              tendrás guardados hasta que completes tu compra.
            </p>
            <Link
              to="/"
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Explorar productos
            </Link>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
            {/* Lista de artículos */}
            <div className="flex flex-col gap-3">
              {items.map((item) => (
                <div
                  key={item.productId}
                  className="glass flex gap-4 rounded-lg p-4"
                >
                  <Link
                    to={`/producto/${item.productId}`}
                    className="block h-20 w-20 shrink-0 overflow-hidden rounded-md bg-background-100"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-foreground-300">
                        <i className="ri-image-line text-2xl"></i>
                      </span>
                    )}
                  </Link>

                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/producto/${item.productId}`}
                        className="line-clamp-2 text-sm font-medium text-foreground-900 hover:text-primary-600"
                      >
                        {item.title}
                      </Link>
                      <button
                        type="button"
                        onClick={() => removeItem(item.productId)}
                        aria-label="Quitar del carrito"
                        className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-accent-100 hover:text-accent-700"
                      >
                        <i className="ri-delete-bin-6-line text-base"></i>
                      </button>
                    </div>

                    <div className="mt-auto flex items-center justify-between gap-2 pt-2">
                      <span className="font-heading text-base font-bold text-foreground-950">
                        {formatPrice(item.price * item.quantity, item.currency)}
                      </span>

                      {/* Selector de cantidad */}
                      <div className="flex items-center gap-1 rounded-md border border-background-200 bg-background-50 px-1 py-0.5">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                          aria-label="Disminuir cantidad"
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-600 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="w-7 text-center text-sm font-semibold text-foreground-900">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                          aria-label="Aumentar cantidad"
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-600 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Resumen del pedido */}
            <aside className="h-fit rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                Resumen del pedido
              </h2>

              <div className="mt-4 flex flex-col gap-3 text-sm">
                <div className="flex items-center justify-between text-foreground-600">
                  <span>Artículos ({totalItems})</span>
                  <span>{formatPrice(totalPrice, currency)}</span>
                </div>
                <div className="flex items-center justify-between text-foreground-600">
                  <span>Envío</span>
                  <span className="font-semibold text-secondary-600">Gratis</span>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-4">
                <span className="text-sm font-semibold text-foreground-950">
                  Total
                </span>
                <span className="font-heading text-xl font-bold text-primary-600">
                  {formatPrice(totalPrice, currency)}
                </span>
              </div>

              {error && (
                <p className="mt-3 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-100 px-3 py-2 text-xs font-medium text-accent-800">
                  <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={handleCheckout}
                disabled={checkingOut}
                className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {checkingOut ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="ri-secure-payment-line"></i>
                    Finalizar compra
                  </>
                )}
              </button>

              <p className="mt-3 text-center text-xs text-foreground-400">
                Al finalizar, tu compra queda registrada y el carrito se vacía.
              </p>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}